import React, { useState } from 'react';
import { theme } from '../style';
import { INPUT_WIDTH, LEVEL_WIDTH, AI_TOOLTIP } from '../const';
import Lottie from 'lottie-react';
import robot from '../lottie/Robot.json';
import { MoreInfo } from '../MoreInfo';
import { MdEdit, MdStop } from 'react-icons/md';
import { IoWarning } from 'react-icons/io5';
import { useAI } from '../provider/AiProvider';
import {
  ButtonsWrapper,
  Content,
  GeneratedTextWrapper,
  HTMLPreview,
  ImageWrap,
  InputRow,
  InputsWrapper,
  InputWrapper,
  Label,
  LabelWrapper,
  LevelLabel,
  LevelWrapper,
  MobileLabelWrapper,
  PromptLimitInfo,
  ScriptInput,
  ScriptInputWrapper,
  ScriptLoading,
  SidebarWrapper,
  SlidersWrapper,
  SliderWrapper,
  Textarea,
} from './style';
import {
  DETAIL_LEVEL,
  ENTHUSIASM_LEVEL,
  FORMALITY_LEVEL,
  GetQuickshareParams,
  GPT_RESULT_TEXT_TYPE,
  InventoryItem,
  promptUseCaseOptions,
  PROMPT_TYPE,
} from '../types';
import { AutomotiveInput } from '../input';
import { Dropdown } from '../dropdown';
import { VehicleSearch } from '../VehicleSearch';
import { Slider } from '../Slider';
import { IconSparkle } from '../IconSparkle';
import { Button, useCovideoTheme } from 'react-covideo-common';
import { LandingPageCard } from '../LandingPageCard';
import { useAuth } from '../../provider/auth';
import { NewModal } from '../NewModal';
import { ModalShortUrlMarkAsSent } from './ModalShortUrlMarkAsSent';
import { useShortUrlMarkAsSent } from '../../hooks/useShortUrlQuickShare';

interface Props {
  closeModal: () => void;
  handleSubmitGeneratedContent?:
    | ((
        content: string,
        url?: string,
        thumbnail?: string,
        markAsSentConfirmed?: boolean,
        params?: GetQuickshareParams | undefined
      ) => Promise<void>)
    | ((
        content: string,
        url?: string,
        thumbnail?: string,
        markAsSentConfirmed?: boolean,
        params?: GetQuickshareParams | undefined
      ) => void);
  handleOnCopySuccess?:
    | ((
        content: string,
        url: string,
        thumbnail: string,
        markAsSentConfirmed: boolean,
        params?: GetQuickshareParams | undefined
      ) => Promise<void>)
    | ((
        content: string,
        url: string,
        thumbnail: string,
        markAsSentConfirmed: boolean,
        params?: GetQuickshareParams | undefined
      ) => void);
  parentSelector?: string;
}

export const ModalGenerateText = ({
  closeModal,
  handleSubmitGeneratedContent,
  handleOnCopySuccess,
}: Props) => {
  const covideoTheme = useCovideoTheme();
  const {
    promptType,
    promptUseCase,
    changePromptUseCase,
    isLoading,
    generateContentWithAi,
    promptsLeft,
    generatedText,
    changeVehicleTitle,
    changeVin,
    crmData,
    sender,
    changeSender,
    senderCompany,
    changeSenderCompany,
    recipient,
    changeRecipient,
    vin,
    nextStep,
    changeNextStep,
    changeFormalityLevel,
    detailLevel,
    changeDetailLevel,
    changeEnthusiasmLevel,
    changeAdditionalInfo,
    additionalInfo,
    changeGeneratedText,
    changeBaseText,
    templateOptions,
    selectedTemplateOption,
    changeSelectedTemplateOption,
    linksetOptions,
    selectedLinksetOption,
    changeSelectedLinksetOption,
    overlayOptions,
    selectedOverlayOption,
    changeSelectedOverlayOption,
    generatedThumnail,
    generatedUrl,
    isStreamingResponse,
    handleAbort,
    videoId,
  } = useAI();
  const { userData } = useAuth();
  const [showModalShortUrlMarkAsSent, setShowModalShortUrlMarkAsSent] =
    useState(false);
  const [type, setType] = useState<'copy' | 'insert' | null>(null);
  const handleShortUrlMarkAsSentSuccessCallback = () => {
    closeModal();
  };

  const {
    onShortUrlMarkAsSentMutationHandler,
    isLoadingShortUrlMarkAsSent,
    handleMarkAsSentFlow,
  } = useShortUrlMarkAsSent({
    setShowModalShortUrlMarkAsSent,
    handleShortUrlMarkAsSentSuccessCallback,
    data: { generatedText, generatedUrl, generatedThumnail },
    handleOnCopySuccess: handleOnCopySuccess,
    handleSubmitGeneratedContent: handleSubmitGeneratedContent,
  });

  const { isAutomotiveSalesRole, customer } = userData;
  const submitGeneratedText = async () => {
    setType('insert');
    if (!!crmData?.data && promptType === PROMPT_TYPE.CRM_MESSAGE) {
      handleMarkAsSentFlow({
        videoId: videoId,
        toastType: 'insert',
      });
      return;
    }
    if (!handleSubmitGeneratedContent) {
      return;
    }
    await handleSubmitGeneratedContent(
      generatedText,
      generatedUrl,
      generatedThumnail
    );
    closeModal();
  };

  const onChangeVehicle = (item: InventoryItem) => {
    if (!item) {
      changeVehicleTitle('');
      changeVin('');
      return;
    }
    if (!item.itemId) {
      changeVehicleTitle(item.vin || '');
      changeVin('');
      return;
    }
    changeVehicleTitle(item.title || '');
    changeVin(item.vin || '');
  };

  const copyToClipboard = async () => {
    setType('copy');
    if (!!crmData?.data) {
      handleMarkAsSentFlow({
        videoId: videoId,
        toastType: 'copy',
      });
    }
  };

  const enableCovideoMagic = !!Number(customer.hasCovideoMagic);

  if (!enableCovideoMagic) {
    return null;
  }

  const textType: GPT_RESULT_TEXT_TYPE =
    promptType === PROMPT_TYPE.CRM_MESSAGE
      ? GPT_RESULT_TEXT_TYPE.HTML
      : GPT_RESULT_TEXT_TYPE.PLAIN;

  const showCopyToClipboard = promptType === PROMPT_TYPE.CRM_MESSAGE;
  const isTeleprompter = promptType === PROMPT_TYPE.TELEPROPTER_SCRIPT;
  const showSubmit = !!handleSubmitGeneratedContent;
  const showAdditionalInfoInput =
    promptType !== PROMPT_TYPE.SMS && detailLevel >= DETAIL_LEVEL.MID;

  const outOfPrompts = promptsLeft <= 0;

  const isGenerateDisabled =
    isLoading ||
    outOfPrompts ||
    isStreamingResponse ||
    isLoadingShortUrlMarkAsSent;
  const isSubmitAndCopyDisabled =
    isLoading || outOfPrompts || !generatedText.length || isStreamingResponse;
  const showShareSettings = promptType === PROMPT_TYPE.CRM_MESSAGE;

  return (
    <NewModal
      closeModal={closeModal}
      headerText={`Generate ${promptType} with Covideo AI Assist`}
      style={{
        content: {
          width: '1120px',
        },
      }}
    >
      <Content disabled={outOfPrompts} className="ai-assist">
        <SidebarWrapper>
          <InputsWrapper>
            {showShareSettings && (
              <LandingPageCard
                templateOptions={templateOptions}
                selectedTemplate={selectedTemplateOption}
                setSelectedTemplate={changeSelectedTemplateOption}
                ctaOptions={linksetOptions}
                selectedCta={selectedLinksetOption}
                setSelectedCta={changeSelectedLinksetOption}
                overlaysOptions={overlayOptions}
                selectedOverlay={selectedOverlayOption}
                setSelectedOverlay={changeSelectedOverlayOption}
                extraStyle={{
                  label: { color: '#9297a2', marginTop: 0 },
                  wrapper: { marginTop: 0 },
                  control: { minHeight: 44, maxHeight: 64 },
                }}
              />
            )}
            <InputRow>
              <InputWrapper>
                <Label>Your name</Label>
                <AutomotiveInput
                  radius="6px"
                  value={sender}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    changeSender(e.target.value)
                  }
                  placeholder="Enter your name"
                />
              </InputWrapper>
              <InputWrapper>
                <Label>Company Name</Label>
                <AutomotiveInput
                  radius="6px"
                  value={senderCompany}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    changeSenderCompany(e.target.value)
                  }
                  placeholder="Enter your company name"
                />
              </InputWrapper>
            </InputRow>
            <InputRow>
              <InputWrapper>
                <LabelWrapper>
                  <Label>Message category</Label>
                  <MoreInfo text={AI_TOOLTIP.MESSAGE_CATEGORY} />
                </LabelWrapper>
                <Dropdown
                  width={INPUT_WIDTH}
                  value={promptUseCaseOptions.find((option) => {
                    return option && option.value === promptUseCase;
                  })}
                  options={promptUseCaseOptions}
                  onChange={(option) => changePromptUseCase(option.value)}
                  placeholder="Enter use case"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  menuPlacement="bottom"
                  extendStyles={{
                    container: {
                      width: '100%',
                    },
                    control: {
                      width: '100%',
                    },
                  }}
                  formatCreateLabel={(inputValue) => (
                    <div
                      style={{
                        color: covideoTheme.colors.primary[100],
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <MdEdit color={theme.palette.blue40} /> &nbsp;Write{' '}
                      {`"${inputValue}"`}
                    </div>
                  )}
                />
              </InputWrapper>
              {!isTeleprompter && (
                <InputWrapper>
                  <LabelWrapper>
                    <Label>Their name</Label>
                    <MoreInfo text={AI_TOOLTIP.CUSTOMER_NAME} />
                  </LabelWrapper>
                  <AutomotiveInput
                    radius="6px"
                    value={recipient}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      changeRecipient(e.target.value)
                    }
                    placeholder="Enter customer name"
                  />
                </InputWrapper>
              )}
            </InputRow>
            <InputRow>
              {isAutomotiveSalesRole && (
                <InputWrapper>
                  <LabelWrapper>
                    <Label>Vehicle</Label>
                  </LabelWrapper>
                  <VehicleSearch
                    onChangeVehicle={onChangeVehicle}
                    selectedVin={vin}
                    fetchSold={false}
                    creatable={promptType !== PROMPT_TYPE.CRM_MESSAGE}
                    placeholder="Search vehicles..."
                    borderRadius="6px"
                    dropdownOptionLabelMaxWidth="95%"
                    menuZindex={9999}
                    limitedSingleValueWidth="218px"
                  />
                </InputWrapper>
              )}
              <InputWrapper>
                <LabelWrapper>
                  <Label>Next steps</Label>
                  <MoreInfo
                    text={AI_TOOLTIP.CTA}
                    style={{
                      content: {
                        marginLeft: '50px',
                      },
                    }}
                  />
                </LabelWrapper>
                <AutomotiveInput
                  radius="6px"
                  value={nextStep}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    changeNextStep(e.target.value)
                  }
                  placeholder="Enter next steps"
                />
              </InputWrapper>
            </InputRow>

            <SlidersWrapper>
              <LevelWrapper>
                <MobileLabelWrapper>
                  <LevelLabel display="block">Casual</LevelLabel>
                  <LevelLabel textalign="right" display="block">
                    Formal
                  </LevelLabel>
                </MobileLabelWrapper>
                <LevelLabel>Casual</LevelLabel>
                <SliderWrapper>
                  <Slider
                    marks
                    defaultValue={FORMALITY_LEVEL.MID}
                    min={FORMALITY_LEVEL.LOW}
                    max={FORMALITY_LEVEL.HIGH}
                    onChange={(value) => changeFormalityLevel(value)}
                    width={`${LEVEL_WIDTH}px`}
                  />
                </SliderWrapper>
                <LevelLabel textalign="right">Formal</LevelLabel>
              </LevelWrapper>
              <LevelWrapper>
                <MobileLabelWrapper>
                  <LevelLabel display="block">Reserved</LevelLabel>
                  <LevelLabel textalign="right" display="block">
                    Enthusiastic
                  </LevelLabel>
                </MobileLabelWrapper>
                <LevelLabel>Reserved</LevelLabel>
                <SliderWrapper>
                  <Slider
                    marks
                    defaultValue={ENTHUSIASM_LEVEL.MID}
                    min={ENTHUSIASM_LEVEL.LOW}
                    max={ENTHUSIASM_LEVEL.HIGH}
                    onChange={(value) => changeEnthusiasmLevel(value)}
                    width={`${LEVEL_WIDTH}px`}
                  />
                </SliderWrapper>
                <LevelLabel textalign="right">Enthusiastic</LevelLabel>
              </LevelWrapper>
              {promptType !== PROMPT_TYPE.SMS && (
                <LevelWrapper>
                  <MobileLabelWrapper>
                    <LevelLabel display="block">Brief</LevelLabel>
                    <LevelLabel textalign="right" display="block">
                      Detailed
                    </LevelLabel>
                  </MobileLabelWrapper>
                  <LevelLabel>Brief</LevelLabel>
                  <SliderWrapper>
                    <Slider
                      marks
                      defaultValue={DETAIL_LEVEL.MID}
                      min={DETAIL_LEVEL.LOW}
                      max={DETAIL_LEVEL.HIGH}
                      onChange={(value) => changeDetailLevel(value)}
                      width={`${LEVEL_WIDTH}px`}
                    />
                  </SliderWrapper>
                  <LevelLabel textalign="right">Detailed</LevelLabel>
                </LevelWrapper>
              )}
            </SlidersWrapper>

            {showAdditionalInfoInput && (
              <InputRow>
                <InputWrapper>
                  <Textarea
                    value={additionalInfo}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      changeAdditionalInfo(e.target.value)
                    }
                    placeholder={AI_TOOLTIP.ADDITIONAL_INFO}
                  />
                </InputWrapper>
              </InputRow>
            )}
          </InputsWrapper>
          <ButtonsWrapper>
            {!outOfPrompts && (
              <PromptLimitInfo>
                {promptsLeft} prompts left this month
              </PromptLimitInfo>
            )}
            {outOfPrompts && (
              <PromptLimitInfo color={theme.palette.red100}>
                <IoWarning /> <span>0 prompts left this month</span>
              </PromptLimitInfo>
            )}
            {isStreamingResponse && (
              <Button
                text="Stop"
                onClick={handleAbort}
                icon={<MdStop color={theme.palette.white} />}
                variant="primary"
              />
            )}
            {!isStreamingResponse && (
              <Button
                onClick={generateContentWithAi}
                text={'Generate'}
                variant="gradient"
                disabled={isGenerateDisabled}
                icon={<IconSparkle color={theme.palette.white} />}
              />
            )}
          </ButtonsWrapper>
        </SidebarWrapper>
        <GeneratedTextWrapper>
          <ScriptInputWrapper>
            {isLoading && (
              <ScriptLoading>
                <ImageWrap>
                  <Lottie animationData={robot} loop={true} autoplay={true} />
                </ImageWrap>
              </ScriptLoading>
            )}
            {!isLoading && textType === GPT_RESULT_TEXT_TYPE.PLAIN && (
              <ScriptInput
                disabled={isLoading}
                value={generatedText}
                onChange={(event) => {
                  changeGeneratedText(event.target.value);
                  changeBaseText(event.target.value);
                }}
              />
            )}
            {!isLoading && textType === GPT_RESULT_TEXT_TYPE.HTML && (
              <HTMLPreview
                disabled={isLoading}
                dangerouslySetInnerHTML={{ __html: generatedText }}
              />
            )}
          </ScriptInputWrapper>
          <ButtonsWrapper>
            {showCopyToClipboard && (
              <Button
                onClick={copyToClipboard}
                text="Copy"
                disabled={
                  isSubmitAndCopyDisabled || isLoadingShortUrlMarkAsSent
                }
              />
            )}
            {showSubmit && (
              <Button
                onClick={submitGeneratedText}
                text="Insert This"
                disabled={
                  isSubmitAndCopyDisabled || isLoadingShortUrlMarkAsSent
                }
              />
            )}
          </ButtonsWrapper>
        </GeneratedTextWrapper>
      </Content>
      {showModalShortUrlMarkAsSent && (
        <ModalShortUrlMarkAsSent
          shortUrlMarkAsSentPayload={{
            videoId: videoId,
            toastType: type,
          }}
          isLoadingShortUrlMarkAsSent={isLoadingShortUrlMarkAsSent}
          onShortUrlMarkAsSentMutationHandler={
            onShortUrlMarkAsSentMutationHandler
          }
          handleModalClose={() => {
            setShowModalShortUrlMarkAsSent(false);
          }}
        />
      )}
    </NewModal>
  );
};
